import type {ReactElement} from 'react';
import {useCallback, useMemo, useRef} from 'react';
import type {AiPromptBox, SelectedContextualMenuItems} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxContextualMenu} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu';
import type {ContextualMenuGroup} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {ContextualMenuGroupType} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import type {FooterLeftAction} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {flattenContextualMenuOptions} from '@Components/ai-generation-box/ai-generation-box.library';
import {Text, TextSize} from '@Components/text';
import type {AiGeneratedContent, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {HashtagUsage} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';
import type {SocialPlatform} from '@Components/social-media/social-media.types';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {getPromptBoxOptions} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {createGenerateCaptionsFunctionWithContext, createUseFunctionWithContext, requestRecentCaptions, requestReuseCaption} from '@Libraries/auto-caption-library';
import {useAiWriterPersistOnSelect} from '@Components/ai-writer/hooks/use-ai-writer-persist-on-select';
import {getLengthAndToneContextualMenuGroups, reuseOrUseGeneratedContent} from '@Components/ai-writer/ai-writer.library';
import type {AiWriterSource} from '@Components/ai-writer/ai-writer.types';
import {
  CAPTION_WRITER_CONTEXTUAL_MENU_WIDTH,
  CAPTION_WRITER_PROMPT_BOX_ID,
  CAPTION_WRITER_SESSION_STORAGE_KEY,
  CAPTION_WRITER_USED_IDS_SESSION_STORAGE_KEY,
} from '@Components/social-media-wizard/components/social-post-caption/components/caption-writer-popover-view/caption-writer-popover-view.types';

interface UseCaptionWriterParams {
  accountId: number;
  accountType: SocialPlatform;
  postId: string;
  onContentSelected: (item: AiGeneratedContent) => void;
}

const HASHTAGS_GROUP_KEY = 'hashtags';

const getContextualMenuGroups = (): ContextualMenuGroup[] => {
  return [
    ...getLengthAndToneContextualMenuGroups(),
    {
      label: window.i18next.t('pmwjs_hashtags'),
      key: HASHTAGS_GROUP_KEY,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {label: window.i18next.t('pmwjs_use_hashtags'), value: HashtagUsage.USE_HASHTAGS, active: true},
        {label: window.i18next.t('pmwjs_no_hashtags'), value: HashtagUsage.NO_HASHTAGS, active: false},
      ],
    },
  ];
};

export function useCaptionWriter({accountId, accountType, postId, onContentSelected}: UseCaptionWriterParams): AiWriterSource<AiGeneratedContent> {
  const lastPromptRef = useRef<string>('');

  const initialConfig = useMemo((): AiPromptBox => {
    return {
      ...getPromptBoxOptions(),
      placeholder: window.i18next.t('pmwjs_caption_about_ellipsis'),
      contextualMenu: getContextualMenuGroups(),
      promptBoxState: PromptBoxState.ACTIVE,
    };
  }, []);

  const fetchRecents = useCallback((): Promise<AiGeneratedContent[]> => {
    return requestRecentCaptions();
  }, []);

  const generateCaptions = useMemo(() => createGenerateCaptionsFunctionWithContext(accountId, postId), [accountId, postId]);
  const useCaption = useMemo(() => createUseFunctionWithContext(accountId, postId), [accountId, postId]);

  const onGenerate = useCallback(
    async (prompt: string, options: SelectedContextualMenuItems, originator: GenerateIdeasOriginator): Promise<AiGeneratedContent[]> => {
      lastPromptRef.current = prompt;

      const captions = await generateCaptions(prompt, flattenContextualMenuOptions(options) as SelectedValueType, originator);
      trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_AICAPTIONS_PLATFORMCAPTION, {[GA4EventParamName.PLATFORM]: getSocialPlatformName(accountType)});

      return captions;
    },
    [generateCaptions, accountType]
  );

  const persist = useCallback(
    async (item: AiGeneratedContent, prompt: string): Promise<void> => {
      await reuseOrUseGeneratedContent({
        storageKey: CAPTION_WRITER_USED_IDS_SESSION_STORAGE_KEY,
        lookupKey: item.text,
        existingKey: item.id,
        reuse: requestReuseCaption,
        use: async () => String(await useCaption(prompt || lastPromptRef.current, item.configOptions, item)),
      });
    },
    [useCaption]
  );

  const onItemSelect = useAiWriterPersistOnSelect<AiGeneratedContent>({persist, onSelected: onContentSelected});

  const renderItem = useCallback((item: AiGeneratedContent): ReactElement => {
    return <Text val={item.text} size={TextSize.MD} className="content-body" />;
  }, []);

  const getContextualMenu: FooterLeftAction = useCallback((props): ReactElement => {
    return <AiPromptBoxContextualMenu {...props} maxContextualMenuWidth={CAPTION_WRITER_CONTEXTUAL_MENU_WIDTH} renderTooltip />;
  }, []);

  return useMemo(
    () => ({
      id: CAPTION_WRITER_PROMPT_BOX_ID,
      sessionStorageKey: CAPTION_WRITER_SESSION_STORAGE_KEY,
      initialConfig,
      options: [getContextualMenu],
      fetchRecents,
      onGenerate,
      onItemSelect,
      getItemKey: (item: AiGeneratedContent) => item.text,
      renderItem,
      aiPromptBoxPreviewText: window.i18next.t('pmwjs_caption_about_ellipsis'),
      title: window.i18next.t('pmwjs_write_with_ai'),
      showRetryOnError: true,
    }),
    [initialConfig, getContextualMenu, fetchRecents, onGenerate, onItemSelect, renderItem]
  );
}
